import Button from '@berlitz/button'
import MyBerlitzLesson from '@classes/Lesson'
import PortalPrograms from '@classes/Programs'
import SatisfactionSurvey from '@classes/SatisfactionSurvey'
import EmptyState from '@components/EmptyState'
import { BasicModal } from '@components/Modal'
import SelfTechCheck from '@components/SelfTechCheck'
import WithUserTime from '@hocs/WithUserTime'
import { StoreProps } from '@interfaces/StoreState'
import { PUT_SCHEDULES } from '@mutations/index'
import { MY_CALENDAR_DATA } from '@queries/index'
import { DATE_FORMAT } from '@utils/constants'
import filter from 'lodash/filter'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { Mutation } from 'react-apollo'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import ScheduleListCard from '../ScheduleListCard'
import { IScheduleList } from '../ScheduleListCard/interface'
import useRefetchProfile from '@hooks/useRefetchProfile'
import { GroupedLessons, HeaderWrapper, LessonCount, SchedBox, SelfTechCheckWrapper, StyledH2 } from './style'
import { isEmpty, omitBy, noop, pick, flatten } from 'lodash'
import { GET_EVENT_ATTENDEE } from '@queries/events'
import { IGetEventAttendee, IGetEventAttendeeData, IGetEventsInput } from '@interfaces/Events'
import { useApolloClient } from '@apollo/react-hooks'
import ScheduleListLoader from './Loader'
import PortalRegistrations from '@classes/Registrations'
import { utcfyDate } from '@layouts/Events/helpers'
import CalendarItemEvent from '@berlitzplatforms/micro.ui.calendar-item-event'
import useFormattedMessage from '@hooks/useFormattedMessage'
import { TRANSLATIONS } from '@layouts/Events/constants'
import { useRouter } from 'next/router'
import PortalBerlitzEventAttendee from '@classes/BerlitzEventAttendee'
import ModalCancelSeat from '@layouts/Events/ModalCancelSeat'
import { usePortalFeatures } from '@components/RoleBasedView'
import { ATTENDEE_STATUS } from '@utils/constants/events'
import Spacer from '@berlitz/spacer'
import { convertMomentToDate } from '@utils/helpers'
import InvisibleText from '@components/InvisibleText'
import EventJoinOnlineBtn from '@layouts/Events/EventJoinOnlineBtn'
import { ApolloQueryResult } from '@apollo/client'
import { EventOrientationContext } from '@layouts/Events/context/EventOrientation'
import useTimeFormat from '@hooks/useTimeFormat'
import HeaderButtons from './HeaderButtons'

const ScheduleList: React.FC<IScheduleList> = ({
  availableSchedules,
  onViewMonth,
  variables,
  selectedMonth,
  loading,
  getUserDate,
  onMonthChange,
  toUserDate,
  refetch,
  displayPastEvents = false,
  selected,
}) => {
  const intl = useIntl()
  const router = useRouter()
  const { state } = usePortalFeatures({
    configId: 'c::sidenav::events',
  })
  const { isTimeFormatDisabled, convertToUserTimeFormats } = useTimeFormat()
  const translations = {
    'Delivery language:': `${useFormattedMessage(TRANSLATIONS.DELIVERY_LANGUAGE)}:`,
    'More info': useFormattedMessage(TRANSLATIONS.MORE_INFO),
    'Self tech check': useFormattedMessage(TRANSLATIONS.SELF_TECH_CHECK),
    'Join online': useFormattedMessage(TRANSLATIONS.JOIN_ONLINE),
    Cancel: useFormattedMessage(TRANSLATIONS.CANCEL),
    'Reserve seat': useFormattedMessage(TRANSLATIONS.RESERVE_SEAT),
    "Why can't I reserve a seat?": useFormattedMessage(TRANSLATIONS.WHY_CANT_RESERVE),
    'Seat reserved': useFormattedMessage(TRANSLATIONS.SEAT_RESERVED),
    Full: useFormattedMessage(TRANSLATIONS.FULL),
  }

  const client = useApolloClient()
  const eventOrient = useContext(EventOrientationContext)
  const { StudentPrograms, Type, UserTZ, Registrations } = useSelector(({ userProfile }: StoreProps) => {
    const { Type, StudentProfile, Timezone } = userProfile.info
    const Registrations = new PortalRegistrations(
      userProfile.info?.StudentProfile?.StudentProfileInfo?.RegistrationWrappers
    )

    return {
      Type,
      UserTZ: Timezone,
      StudentPrograms: new PortalPrograms(StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? []),
      Registrations,
    }
  })
  const [schedulesState, setSchedules] = useState(availableSchedules)

  // modals
  const [modalSuccessOpen, setModalSuccessOpen] = useState(false)
  const [openCancelModal, setOpenCancelModal] = useState(false)

  const [selectedEvent, setSelectedEvent] = useState<PortalBerlitzEventAttendee | undefined>()
  const refetchProfileAfter = useRefetchProfile()

  const [eventLoading, setEventLoading] = useState(false)
  const [userEvents, setUserEvents] = useState<IGetEventAttendee[]>([])

  const getUserEvents = async (defaultPayload: IGetEventsInput) => {
    setEventLoading(true)

    const RecordIds = Registrations?.getEventAtteendeesPayload(defaultPayload) || []

    const promises: Promise<ApolloQueryResult<IGetEventAttendeeData>>[] = await RecordIds?.map((variables) =>
      client.query({
        query: GET_EVENT_ATTENDEE,
        fetchPolicy: 'network-only',
        variables,
      })
    )

    const res: ApolloQueryResult<IGetEventAttendeeData>[] = await Promise.all(promises)
    setEventLoading(false)
    setUserEvents(
      flatten(
        (res || [])?.map(({ data }, index) =>
          (data?.getEventAttendeev2 || []).map((event) => ({
            ...event,
            berlitzEventAttendeeInfo: {
              ...event.berlitzEventAttendeeInfo,
              RecordId: RecordIds[index]?.RecordId,
            },
          }))
        )
      )
    )
  }

  useEffect(() => {
    let unmounted = false

    if (!unmounted && state.allowed) {
      getUserEvents({
        StartDateTime: utcfyDate(moment(variables?.FromDate || '')),
        EndDateTime: utcfyDate(moment(variables?.EndDate || '')),
        RecordId: '',
        Type,
      })
    }

    return () => {
      unmounted = true
    }
  }, [state.allowed, variables?.FromDate, variables?.EndDate])

  useEffect(() => {
    if (!loading && !(schedulesState || []).filter(({ Status }) => Status !== 'Cancelled').length) {
      refetchProfileAfter().then((res) => {
        const updateUI = res[0] || noop
        updateUI()
        refetch()
      })
    }
  }, [schedulesState])

  if (loading || eventLoading) {
    return <ScheduleListLoader />
  }

  if (!isEqual(availableSchedules, schedulesState)) {
    setSchedules(availableSchedules)
  }

  const handleCancelLesson = (SessionId) => {
    setSchedules(
      schedulesState.map((sched) => {
        sched.Status = sched.SessionId === SessionId ? 'Cancelled' : sched.Status
        return sched
      })
    )
    setModalSuccessOpen(true)
  }
  const groupedObj = groupBy(
    filter(schedulesState, ({ Status }) => Status !== 'Cancelled'),
    (el) => toUserDate(el.StartTime, DATE_FORMAT.TZ, DATE_FORMAT.BASIC)
  )
  const groupedEventObj = state.allowed
    ? groupBy(
        (userEvents || [])
          .filter(
            ({ berlitzEventAttendeeInfo }) => berlitzEventAttendeeInfo.AttendeeStatus !== ATTENDEE_STATUS.CANCELLED
          )
          .map((event) => ({
            ...event,
            StartTime: event.berlitzEventAttendeeInfo.StartDatetime,
            EndTime: event.berlitzEventAttendeeInfo.EndDatetime,
          })),
        (el) => toUserDate(el.StartTime, DATE_FORMAT.TZ, DATE_FORMAT.BASIC)
      )
    : {}
  let groupedByDateObj: string[] = Object.keys({ ...groupedObj, ...groupedEventObj }).filter(
    (date) => selectedMonth === moment(date).format(DATE_FORMAT.MMMM)
  )

  let groupedByDate = groupedByDateObj.reduce(
    (res, key) => (
      (res[key] = [...(groupedObj[key] || []), ...(groupedEventObj[key] || [])].filter(
        ({ EndTime }) => displayPastEvents || moment.tz(UserTZ).isBefore(EndTime)
      )),
      res
    ),
    {}
  )

  if (selected) {
    groupedByDate = pick(groupedByDate, [moment(selected).format('YYYY-MM-DD')])
  }

  groupedByDate = omitBy(groupedByDate, (val: any[] = []) => !val.length)

  return (
    <Mutation mutation={PUT_SCHEDULES}>
      {(putSchedules) => {
        const schedules = sortBy(Object.keys(groupedByDate), (el) => el.replace('-', ''))

        if (!schedules.length) {
          const currentDate = convertMomentToDate(moment().tz(UserTZ).add(1, 'month').startOf('month'))

          return (
            <>
              <HeaderWrapper>
                <HeaderButtons
                  hasValidProgram={StudentPrograms.HasValidPrograms}
                  onViewMonth={onViewMonth}
                  selectedMonth={selectedMonth}
                  refetch={refetch}
                />
              </HeaderWrapper>
              <Spacer size="xl" />
              <EmptyState heading="You have no upcoming lessons for this month." img="/images/empty-events-24.svg">
                <Button onClick={() => onMonthChange(currentDate)}>
                  <FormattedMessage id="View next month" />
                </Button>
              </EmptyState>
              <Spacer size="xxxl" />
            </>
          )
        }

        return (
          <>
            <HeaderWrapper>
              <HeaderButtons
                hasValidProgram={StudentPrograms.HasValidPrograms}
                onViewMonth={onViewMonth}
                selectedMonth={selectedMonth}
                refetch={refetch}
              />
            </HeaderWrapper>

            {schedules.map((date) => {
              const grouped = (groupedByDate[date] || []).sort(
                // eslint-disable-next-line radix
                (a, b) => parseInt(moment(a.StartTime).format('x')) - parseInt(moment(b.StartTime).format('x'))
              )

              const groupedSessions = groupBy(grouped, (el) => el.SessionId || el?.berlitzEventAttendeeInfo?.EventId)
              const sessionIds = Object.keys(groupedSessions)
              const survey = new SatisfactionSurvey(grouped, StudentPrograms)

              return (
                <SchedBox key={date}>
                  <StyledH2 selected={getUserDate().format('YYYY-MM-DD') === date}>
                    <span data-e2e="schedule-row-date">{moment(date).format(DATE_FORMAT.DEFAULT)}</span>
                  </StyledH2>

                  {sessionIds.map((sessionId) => {
                    const lessons = groupedSessions[sessionId].length
                    const isGroup = lessons > 1
                    const firstLesson = groupedSessions[sessionId][0]

                    const FirstLesson = new MyBerlitzLesson(
                      firstLesson,
                      StudentPrograms.getProgram(firstLesson?.ProgramId)
                    )

                    const moreSiblingBtns = isGroup && FirstLesson.isCancellable() && FirstLesson.showRescheduleButton()

                    return (
                      <GroupedLessons isGroup={isGroup} key={sessionId}>
                        {isGroup && (
                          <>
                            <LessonCount moreSiblingBtns={moreSiblingBtns}>
                              {lessons} <FormattedMessage id="lessons" />
                            </LessonCount>
                            <SelfTechCheck {...firstLesson} Wrapper={SelfTechCheckWrapper} />
                          </>
                        )}
                        {groupedSessions[sessionId].map((lesson, index) => {
                          if (state.allowed && lesson.berlitzEventAttendeeInfo) {
                            const Event = new PortalBerlitzEventAttendee(lesson, false, UserTZ)
                            const {
                              MyCalendarStatus,
                              Type,
                              Language,
                              Date,
                              getTimeWithPortalTimeFormat,
                              SeatsLeft,
                              Id,
                              AttendeeId,
                            } = Event

                            const EventType = eventOrient.getType(
                              Type,
                              '',
                              Event?.event?.berlitzEventAttendeeInfo?.RecordId
                            )
                            const eRecId = Event?.event?.berlitzEventAttendeeInfo?.RecordId
                            const recId = eRecId ? `&recordId=${eRecId}` : ''
                            return (
                              <>
                                <InvisibleText>
                                  <div>Id: {Id}</div>
                                  <div>EventId: {Event?.event.berlitzEventAttendeeInfo.EventId}</div>
                                  <div>DisplayName: {Event?.DisplayName}</div>
                                  <div>AttendeeId: {AttendeeId}</div>
                                  <div>StartDatetime: {Event?.event.berlitzEventAttendeeInfo.StartDatetime}</div>
                                  <div>EndDatetime: {Event?.event.berlitzEventAttendeeInfo.EndDatetime}</div>
                                  <div>EventStatus: {Event?.event.berlitzEventAttendeeInfo.EventStatus}</div>
                                </InvisibleText>

                                <CalendarItemEvent
                                  key={`my-calendar-item-event-${index}-${Id}`}
                                  name={Event?.DisplayName}
                                  status={MyCalendarStatus}
                                  rawType={Event?.RawType}
                                  type={intl.formatMessage({ id: EventType, defaultMessage: EventType })}
                                  language={Language}
                                  date={Date}
                                  time={getTimeWithPortalTimeFormat(isTimeFormatDisabled, convertToUserTimeFormats)}
                                  actionsDisabled={modalSuccessOpen || openCancelModal}
                                  customJoinOnline={
                                    Event?.event.berlitzEventAttendeeInfo ? (
                                      <EventJoinOnlineBtn
                                        berlitzEventAttendeeInfo={Event?.event.berlitzEventAttendeeInfo}
                                      />
                                    ) : undefined
                                  }
                                  translations={{
                                    ...translations,
                                    '{count} seats left': intl.formatMessage(TRANSLATIONS.COUNT_SEATS_LEFT, {
                                      count: SeatsLeft,
                                    }),
                                  }}
                                  onMoreInfo={() => {
                                    router.push(
                                      `/events/?id=${Id}&start=${Event.RawStartDatetime}&end=${Event.RawEndDatetime}${recId}`
                                    )
                                  }}
                                  onCancel={() => {
                                    setSelectedEvent(Event)
                                    setOpenCancelModal(true)
                                  }}
                                />

                                <Spacer size="md" />
                              </>
                            )
                          }

                          const groupDetails = {
                            isFirst: index === 0,
                            isLast: index === groupedSessions[sessionId].length - 1,
                            isGroup,
                            sessionStartTime: firstLesson.StartTime,
                            isFeedbackEnabled: survey.IsFeedbackEnabled(lesson.Id),
                          }
                          return (
                            <ScheduleListCard
                              refetch={refetch}
                              lesson={lesson}
                              key={`lesson-card-${index}-${lesson.Id}`}
                              putSchedules={async (scheduleVariables) => {
                                const { data } = await putSchedules({
                                  variables: scheduleVariables.variables,
                                  refetchQueries: [
                                    {
                                      query: MY_CALENDAR_DATA,
                                      variables,
                                    },
                                  ],
                                  awaitRefetchQueries: true,
                                })
                                return data
                              }}
                              onCancelLesson={handleCancelLesson}
                              {...groupDetails}
                            />
                          )
                        })}
                      </GroupedLessons>
                    )
                  })}
                </SchedBox>
              )
            })}

            <BasicModal
              open={modalSuccessOpen}
              onClose={async () => {
                setModalSuccessOpen(false)

                const [updateUI] = await refetchProfileAfter()
                await updateUI()
                refetch()
              }}
              type="success"
              modalTitle={`${intl.formatMessage({
                id: 'Your lesson has been cancelled',
              })}`}
            />

            <ModalCancelSeat
              EventAttendeeId={selectedEvent?.AttendeeId}
              open={!isEmpty(selectedEvent) && openCancelModal}
              refetchQueries={['GetBerlitzEventAttendee']}
              toggleOpen={(open) => setOpenCancelModal(open)}
              onSuccessClose={() => refetch()}
            />
          </>
        )
      }}
    </Mutation>
  )
}

export default WithUserTime(ScheduleList)
